<template>
    <div
        class="py-2 px-4 items-start grid grid-cols-[10rem_1fr] gap-4"
        :class="{ 'border-b border-b-gray-200': props.hasBorderBottom }"
        :style="gridTemplateColumnsStyle"
    >
        <div class="text-gray-500 flex flex-col">
            <strong class="text-sm font-medium flex flex-row items-center gap-1">
                {{ props.label }}
                <HelpButton v-if="props.helpText" :text="props.helpText" />
            </strong>
            <span class="text-xs font-light">{{ props.labelHint }}</span>
        </div>

        <div
            v-if="imageErrorText"
            class="size-10 shadow-sm border-2 border-red-700 bg-red-200 flex items-center justify-center"
        >
            <HelpButton status="Error" :text="imageErrorText" />
        </div>

        <div v-else class="relative size-10 shadow-sm overflow-hidden">
            <img v-if="props.imageUrl" :src="props.imageUrl" :alt="props.imageAlt" @error="onImageError" />

            <div
                v-if="props.imageState === 'Processing'"
                class="absolute inset-0 flex items-center justify-center bg-white/60"
            >
                <PendingIcon class="size-8 animate-spin text-gray-500" />
                <span class="sr-only">{{ $t('logo-processing') }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useImageError } from '@/utils/useImageError';
import { useFluent } from 'fluent-vue';
import { computed, watch } from 'vue';
import HelpButton from '../HelpButton/HelpButton.vue';
import PendingIcon from '../Icon/PendingIcon.vue';

type CardRowImageProps = {
    label: string;
    labelHint: string;
    labelWidthInRem?: number;
    helpText?: string;
    imageUrl: string;
    imageAlt: string;
    imageState?: 'Default' | 'Processing' | 'Processing-Failed';
    hasBorderBottom: boolean;
    throwOnImageError?: boolean;
};

const props = defineProps<CardRowImageProps>();
const gridTemplateColumnsStyle = computed(() => {
    return `grid-template-columns: ${props.labelWidthInRem ?? 15}rem 1fr;`;
});

const { hasLoadingImageFailed, onImageError, setHasLoadingImageFailed } = useImageError(props.throwOnImageError);

// A new logo can arrive without a reload, an earlier load error must not hide it
watch(
    () => props.imageUrl,
    () => setHasLoadingImageFailed(false),
);

const fluent = useFluent();
const imageErrorText = computed(() => {
    if (props.imageState === 'Processing-Failed') {
        return fluent.$t('logo-processing-failed');
    }
    if (hasLoadingImageFailed.value) {
        return fluent.$t('loading-logo-failed');
    }
    return undefined;
});
</script>
